import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, RefreshCw, Landmark, DollarSign, HandCoins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRoles } from "@/hooks/useRole";
import { toast } from "@/hooks/use-toast";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

const CATEGORIES = [
  "All categories",
  "Health & Wellness",
  "Education",
  "Human Services",
  "Arts, Culture & Humanities",
  "Environment & Animals",
  "Community & Public Benefit",
  "Religion-Related",
  "International",
  "Mutual & Membership",
];

function fmt(n: number | null | undefined) {
  if (n == null) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

function fmtNum(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString();
}

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
  color: "hsl(var(--foreground))",
} as const;

export default function TopFundersTab() {
  const [category, setCategory] = useState("All categories");
  const { isSuperAdmin } = useRoles();
  const [ingesting, setIngesting] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["top-al-funders", category],
    queryFn: async () => {
      let q = supabase
        .from("funder_990_profiles")
        .select("ein, legal_name, city, state, ntee_category, total_giving, fiscal_year")
        .eq("state", "AL")
        .order("total_giving", { ascending: false, nullsFirst: false })
        .limit(50);
      if (category !== "All categories") q = q.eq("ntee_category", category);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["al-funder-stats"],
    queryFn: async () => {
      const { count } = await supabase
        .from("funder_990_profiles")
        .select("ein", { count: "exact", head: true })
        .eq("state", "AL");
      return { total: count ?? 0 };
    },
  });

  // Aggregate trends across AL-HQ funder profiles (giving by FY)
  const { data: trends } = useQuery({
    queryKey: ["al-funder-trends", category],
    queryFn: async () => {
      let q = supabase
        .from("funder_990_profiles")
        .select("fiscal_year, total_giving, grants_count, avg_grant, is_private_foundation, foundation_grants_paid")
        .eq("state", "AL")
        .not("fiscal_year", "is", null);
      if (category !== "All categories") q = q.eq("ntee_category", category);
      const { data, error } = await q;
      if (error) throw error;

      const byYear = new Map<number, { year: number; total_giving: number; awards: number; funders: number; foundation_grants_paid: number }>();
      let pfCount = 0;
      let pfWithGrantsCount = 0;
      let totalFoundationGrantsPaid = 0;
      for (const row of data ?? []) {
        const y = Number(row.fiscal_year);
        if (!y) continue;
        const cur = byYear.get(y) ?? { year: y, total_giving: 0, awards: 0, funders: 0, foundation_grants_paid: 0 };
        cur.total_giving += Number(row.total_giving ?? 0);
        cur.awards += Number(row.grants_count ?? 0);
        cur.funders += 1;
        const fgp = Number(row.foundation_grants_paid ?? 0);
        cur.foundation_grants_paid += fgp;
        byYear.set(y, cur);
        if (row.is_private_foundation) {
          pfCount += 1;
          if (fgp > 0) {
            pfWithGrantsCount += 1;
            totalFoundationGrantsPaid += fgp;
          }
        }
      }
      const series = Array.from(byYear.values()).sort((a, b) => a.year - b.year);
      const totals = series.reduce(
        (acc, r) => ({
          giving: acc.giving + r.total_giving,
          awards: acc.awards + r.awards,
        }),
        { giving: 0, awards: 0 }
      );
      const avg = totals.awards > 0 ? totals.giving / totals.awards : 0;
      const avgFoundationGrantsPaid = pfWithGrantsCount > 0
        ? totalFoundationGrantsPaid / pfWithGrantsCount
        : 0;
      return {
        series,
        totals,
        avg,
        pfCount,
        pfWithGrantsCount,
        totalFoundationGrantsPaid,
        avgFoundationGrantsPaid,
      };
    },
  });

  const topGivers = (data ?? []).slice(0, 10).map((f) => ({
    name: f.legal_name ?? "Unknown",
    short: (f.legal_name ?? "").replace(/\b(Foundation|Charitable|Trust|Inc\.?)\b/gi, "").trim().slice(0, 22),
    total_giving: Number(f.total_giving ?? 0),
  }));

  const runIngest = async () => {
    setIngesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("ingest-990-funders", {
        body: { maxPages: 5, enrich: true },
      });
      if (error) throw error;
      toast({ title: "Ingest complete", description: `Processed ${data?.processed ?? 0} funders.` });
      refetch();
    } catch (e: any) {
      toast({ title: "Ingest failed", description: e.message, variant: "destructive" });
    } finally {
      setIngesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-foreground">
            Top Foundations in Alabama
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Alabama-headquartered private foundations from public IRS Form 990 filings.
            {stats?.total ? ` ${stats.total.toLocaleString()} tracked.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {isSuperAdmin && (
            <Button variant="outline" size="sm" onClick={runIngest} disabled={ingesting}>
              {ingesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Sync 990s</span>
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !data || data.length === 0 ? (
        <div className="rounded-xl border border-border bg-card/50 p-8 text-center">
          <p className="text-muted-foreground">
            No funder data ingested yet.{" "}
            {isSuperAdmin ? "Click \"Sync 990s\" above to pull from ProPublica." : "Check back soon."}
          </p>
        </div>
      ) : (
        <>
        {/* Aggregate stat tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-border bg-card/50 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <DollarSign className="h-3.5 w-3.5" /> Total giving (all FY)
            </div>
            <div className="mt-2 text-2xl font-display font-bold text-foreground tabular-nums">
              {fmt(trends?.totals.giving)}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card/50 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <HandCoins className="h-3.5 w-3.5" /> Foundation grants paid
            </div>
            <div className="mt-2 text-2xl font-display font-bold text-foreground tabular-nums">
              {fmt(trends?.totalFoundationGrantsPaid)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Across {trends?.pfWithGrantsCount ?? 0} AL private foundations
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card/50 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Landmark className="h-3.5 w-3.5" /> Avg per foundation
            </div>
            <div className="mt-2 text-2xl font-display font-bold text-foreground tabular-nums">
              {fmt(trends?.avgFoundationGrantsPaid)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Most recent 990-PF filing
            </div>
          </div>
        </div>

        {/* Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card/50 p-4">
            <h3 className="text-sm font-semibold text-foreground">Giving by year</h3>
            <p className="text-xs text-muted-foreground mb-3">Aggregate total giving across AL-HQ foundations per fiscal year.</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends?.series ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => fmt(v)} width={56} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => [fmt(v), "Total giving"]}
                  />
                  <Bar dataKey="total_giving" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card/50 p-4">
            <h3 className="text-sm font-semibold text-foreground">Foundation grants paid by year</h3>
            <p className="text-xs text-muted-foreground mb-3">Aggregate grants paid by AL-HQ private foundations per fiscal year (990-PF line 25).</p>
            {(trends?.totalFoundationGrantsPaid ?? 0) > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends?.series ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} width={56} tickFormatter={(v) => fmt(v)} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => [fmt(v), "Grants paid"]}
                  />
                  <Line type="monotone" dataKey="foundation_grants_paid" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-center px-6">
                <HandCoins className="h-8 w-8 text-muted-foreground/60 mb-2" />
                <p className="text-sm text-foreground">No AL-HQ private foundations in this filter</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                  Try "All categories" — most foundations don't fall under a single NTEE.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Top 10 by total giving */}
        <div className="rounded-xl border border-border bg-card/50 p-4">
          <h3 className="text-sm font-semibold text-foreground">Top 10 funders by total giving</h3>
          <p className="text-xs text-muted-foreground mb-3">Most recently reported fiscal year per funder.</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topGivers} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => fmt(v)} />
                <YAxis type="category" dataKey="short" stroke="hsl(var(--muted-foreground))" fontSize={11} width={150} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v: number) => [fmt(v), "Total giving"]}
                  labelFormatter={(_, p) => (p?.[0]?.payload?.name ?? "")}
                />
                <Bar dataKey="total_giving" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-card/50">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Funder</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Location</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Category</th>
                <th className="px-4 py-3 text-right">Total giving</th>
                <th className="px-4 py-3 text-right hidden sm:table-cell">FY</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((f) => (
                <tr key={f.ein} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium text-foreground">{f.legal_name}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                    {[f.city, f.state].filter(Boolean).join(", ")}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {f.ntee_category && <Badge variant="secondary" className="text-xs">{f.ntee_category}</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{fmt(Number(f.total_giving))}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground hidden sm:table-cell">{f.fiscal_year ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={`https://projects.propublica.org/nonprofits/organizations/${f.ein}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-primary hover:underline"
                    >
                      990 <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        Source: IRS Form 990 filings via ProPublica Nonprofit Explorer. Total giving reflects the most
        recently ingested filing year and may lag 1–2 years behind current giving.
      </p>
    </div>
  );
}